import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Attractions = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const attractions = [
    {
      title: 'Bahía Inglesa',
      description: 'Esta playa se caracteriza por la transparencia de sus aguas, poco desnivel y escaso oleaje que la han transformado en el centro turístico de verano más concurrido de la tercera región.',
      image: '/images/Bahia-Inglesa.jpg'
    },
    {
      title: 'Desierto de Atacama',
      description: 'El desierto más árido del mundo, con sus salares, termas y geisers, grandes minas de cobre y otros minerales, y fértiles quebradas.',
      image: '/images/desierto-de-atacama.jpg'
    },
    {
      title: 'Volcán Ojos del Salado',
      description: 'Lugares predilectos para los amantes del deporte y la adrenalina, con espectaculares campos dunares para la práctica de off-road y sandboard.',
      image: '/images/volcan-ojos-del-Salado.jpg'
    },
    {
      title: 'Parque Nacional Tres Cruces',
      description: 'A aproximadamente 3800 metros de altura. El Parque Nacional a tres horas de Copiapó viajando en automóvil.',
      image: '/images/Parque-nacional-3cruces.jpg'
    },
    {
      title: 'Desierto Florido',
      description: 'Un fenómeno único donde el desierto despierta de su letargo e irrumpe con un manto de flores de diversos colores.',
      image: '/images/desierto-florido.jpg'
    },
    {
      title: 'Valle de Copiapó',
      description: 'Los valles cubiertos de parronales forman un escenario único en la región, con un clima acogedor y soleado donde se unen pasado, presente y futuro.',
      image: '/images/valle-de-copiapo.jpg'
    },
    {
      title: 'Salar de Pedernales',
      description: 'Enorme extensión de sales, ubicada en una depresión entre montañas. En sus márgenes interiores se forman lagunas de color azul.',
      image: '/images/Salar-de-Pedernales.jpg'
    },
    {
      title: 'Laguna del Negro Francisco',
      description: 'Ubicada en el Parque Nacional Nevado Tres Cruces, a 4.000 m.s.n.m., rodeada de parajes y cumbres importantes.',
      image: '/images/laguna-del-negro-francisco.jpg'
    }
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % attractions.length);
  }, [attractions.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + attractions.length) % attractions.length);
  };

  // Auto-play carousel every 3 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  return (
    <section id="attractions" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
          Atractivos y Actividades
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Descubre los lugares más hermosos de la Región de Atacama
        </p>

        <div 
          className="max-w-5xl mx-auto"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            {/* Contenedor de imagen con alto flexible */}
            <div className="relative min-h-[400px] md:min-h-[500px] lg:min-h-[600px]">
              {attractions.map((attraction, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    currentSlide === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={attraction.image}
                    alt={attraction.title}
                    loading="lazy"
                    className="w-full h-full object-contain bg-gray-100"
                  />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
                <h3 className="text-2xl md:text-4xl font-bold mb-3">
                  {attractions[currentSlide].title}
                </h3>
                <p className="text-base md:text-lg text-white/90 max-w-3xl">
                  {attractions[currentSlide].description}
                </p>
              </div>
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-2 md:p-3 rounded-full transition-all duration-200 z-10"
              data-testid="attractions-prev-btn"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-2 md:p-3 rounded-full transition-all duration-200 z-10"
              data-testid="attractions-next-btn"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          <div className="flex justify-center mt-6 gap-2 flex-wrap">
            {attractions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index ? 'w-8 bg-blue-900' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                data-testid={`attraction-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Attractions;